/**
 * Production Page
 *
 * Main page for managing production orders
 * Features:
 * - List view with search and filtering
 * - Kanban board view
 * - Production order lifecycle management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  RefreshCw,
  Search,
  Loader2,
  Factory,
  Eye,
  Play,
  CheckCircle,
  XCircle,
  List,
  Columns,
  X,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import productionService from '../services/productionService';

function ProductionPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load user info on mount
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load production orders
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        page_size: pageSize,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await productionService.getProductionOrders(params);
      setOrders(response.items || []);
      setTotalItems(response.total || 0);
    } catch (err) {
      console.error('Failed to load production orders:', err);
      setError('Failed to load production orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, statusFilter]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const statsData = await productionService.getProductionStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load production stats:', err);
    }
  }, []);

  useEffect(() => {
    loadOrders();
    loadStats();
  }, [loadOrders, loadStats, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleCreateOrder = () => {
    navigate('/production/new');
  };

  const handleViewOrder = (orderId) => {
    navigate(`/production/${orderId}`);
  };

  const handleReleaseOrder = async (orderId) => {
    try {
      setLoading(true);
      await productionService.releaseOrder(orderId);
      handleRefresh();
    } catch (err) {
      setError(`Failed to release order: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartProduction = async (orderId) => {
    try {
      setLoading(true);
      await productionService.startProduction(orderId);
      handleRefresh();
    } catch (err) {
      setError(`Failed to start production: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('确定要取消此生产工单吗？')) {
      return;
    }

    try {
      setLoading(true);
      await productionService.cancelOrder(orderId);
      handleRefresh();
    } catch (err) {
      setError(`Failed to cancel order: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'secondary', label: '草稿' },
      released: { variant: 'default', label: '已发布' },
      in_progress: { variant: 'warning', label: '生产中' },
      completed: { variant: 'success', label: '已完成' },
      cancelled: { variant: 'destructive', label: '已取消' },
    };
    const { variant, label } = config[status] || { variant: 'secondary', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { variant: 'outline', label: '低' },
      normal: { variant: 'secondary', label: '普通' },
      high: { variant: 'default', label: '高' },
      urgent: { variant: 'destructive', label: '紧急' },
    };
    const { variant, label } = config[priority] || { variant: 'secondary', label: priority };
    return <Badge variant={variant}>{label}</Badge>;
  };

  // Check user permissions
  const canCreate = user && ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(user.role);
  const canManage = user && ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(user.role);

  const totalPages = Math.ceil(totalItems / pageSize);

  // Statistics cards
  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <Icon className={`h-8 w-8 ${color}`} />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">生产工单</h1>
            <p className="text-muted-foreground">
              管理生产工单，跟踪生产进度
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
            {canCreate && (
              <Button onClick={handleCreateOrder}>
                <Plus className="h-4 w-4 mr-2" />
                新建工单
              </Button>
            )}
          </div>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="工单总数"
              value={stats.total_orders || 0}
              icon={Factory}
              color="text-blue-500"
            />
            <StatCard
              title="生产中"
              value={stats.by_status?.in_progress?.count || 0}
              icon={Play}
              color="text-yellow-500"
            />
            <StatCard
              title="已完成"
              value={stats.by_status?.completed?.count || 0}
              icon={CheckCircle}
              color="text-green-500"
            />
            <StatCard
              title="累计产量"
              value={stats.total_completed || 0}
              icon={Factory}
              color="text-purple-500"
            />
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* View Toggle and Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="搜索工单号、产品代码或名称..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="按状态筛选" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  <SelectItem value="draft">草稿</SelectItem>
                  <SelectItem value="released">已发布</SelectItem>
                  <SelectItem value="in_progress">生产中</SelectItem>
                  <SelectItem value="completed">已完成</SelectItem>
                  <SelectItem value="cancelled">已取消</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('kanban')}
                >
                  <Columns className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders List/Kanban */}
        {viewMode === 'list' ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Factory className="h-5 w-5" />
                生产工单 ({totalItems})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  未找到生产工单。{canCreate && '点击"新建工单"添加。'}
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                     <TableHead>工单号</TableHead>
                     <TableHead>产品</TableHead>
                     <TableHead>客户</TableHead>
                     <TableHead>计划数量</TableHead>
                     <TableHead>完成数量</TableHead>
                     <TableHead>优先级</TableHead>
                     <TableHead>状态</TableHead>
                     <TableHead>来源</TableHead>
                     <TableHead className="text-right">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order._id}>
                          <TableCell className="font-mono">{order.production_order_id}</TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {order.items && order.items.length > 0 ? (
                                  <span>{order.items.length} 个产品</span>
                                ) : (
                                  <span>{order.finished_product_code}</span>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {order.items && order.items.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {order.items.slice(0, 3).map((item, idx) => (
                                      <span key={idx} className="truncate max-w-[150px]">
                                        {item.finished_product_code}
                                      </span>
                                    ))}
                                    {order.items.length > 3 && <span>...</span>}
                                  </div>
                                ) : (
                                  <span>{order.finished_product_name}</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {order.client_name ? (
                              <span className="text-sm">{order.client_name}</span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {order.total_planned_quantity
                              || order.planned_quantity
                              || (order.items
                                ? order.items.reduce((sum, item) => sum + (item.planned_quantity || 0), 0)
                                : '-')}
                          </TableCell>
                          <TableCell>
                            {order.total_completed_quantity
                              || order.completed_quantity
                              || (order.items
                                ? order.items.reduce((sum, item) => sum + (item.completed_quantity || 0), 0)
                                : 0)}
                            {order.scrap_quantity > 0 && (
                              <span className="text-destructive ml-1">
                                ({order.scrap_quantity} 报废)
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{getPriorityBadge(order.priority)}</TableCell>
                          <TableCell>{getStatusBadge(order.status)}</TableCell>
                          <TableCell>
                            {order.source_reference ? (
                              <span className="text-sm text-muted-foreground">
                                {order.source_reference}
                              </span>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewOrder(order._id)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {canManage && order.status === 'draft' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReleaseOrder(order._id)}
                                  title="发布生产"
                                >
                                  <Play className="h-4 w-4" />
                                </Button>
                              )}
                              {canManage && order.status === 'released' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStartProduction(order._id)}
                                  title="开始生产"
                                >
                                  <Factory className="h-4 w-4" />
                                </Button>
                              )}
                              {canManage && !['completed', 'cancelled'].includes(order.status) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancelOrder(order._id)}
                                  className="text-destructive hover:text-destructive"
                                  title="取消工单"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-between items-center mt-4">
                      <div className="text-sm text-muted-foreground">
                        第 {page} 页 / 共 {totalPages} 页（共 {totalItems} 项）
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                        >
                          上一页
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                        >
                          下一页
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Kanban View */
          <div className="grid grid-cols-4 gap-4">
            {['draft', 'released', 'in_progress', 'completed'].map((status) => (
              <Card key={status} className="bg-muted/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    {getStatusBadge(status)}
                    <span className="text-muted-foreground">
                      ({orders.filter((o) => o.status === status).length})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {orders
                    .filter((o) => o.status === status)
                    .map((order) => (
                      <Card
                        key={order._id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleViewOrder(order._id)}
                      >
                        <CardContent className="p-3">
                          <div className="font-mono text-sm">{order.production_order_id}</div>
                          <div className="text-sm text-muted-foreground truncate">
                            {order.finished_product_name}
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-sm">
                              {order.completed_quantity || 0}/{order.planned_quantity}
                            </span>
                            {getPriorityBadge(order.priority)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ProductionPage;
